import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  TOP_DONORS_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/topDonorsColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const buildFilterGroups = (venueOwnerships) => {
  const groups = [];

  if (venueOwnerships && venueOwnerships.length > 0) {
    groups.push({
      label: 'Venues',
      filters: [
        {
          id: 'venue_ownership_id',
          label: 'Venue',
          type: 'checkbox',
          options: venueOwnerships.map((vo) => ({
            value: String(vo.id),
            label: vo.name || vo.venue_name || `Venue ${vo.id}`,
          })),
        },
      ],
    });
  }

  groups.push({
    label: 'Giving',
    filters: [
      { id: 'total_donated', label: 'Total Donated', type: 'range' },
      { id: 'gift_count', label: 'Number of Gifts', type: 'range' },
    ],
  });

  return groups;
};

const rangeParams = (params, filterValue, key) => {
  if (!filterValue) return;
  if (filterValue.min != null && filterValue.min !== '') params[`${key}_min`] = filterValue.min;
  if (filterValue.max != null && filterValue.max !== '') params[`${key}_max`] = filterValue.max;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  rangeParams(params, activeFilters.total_donated, 'total_donated');
  rangeParams(params, activeFilters.gift_count, 'gift_count');
  return params;
};

const TOP_DONORS_CONFIG = {
  pageTitle: 'Top Donors',
  pageSubtitle: 'Donors ranked by total giving. Totals cover attributed donors only and exclude anonymous gifts.',
  sidebarKey: 'donations_top_donors',
  defaultPreset: 'year_to_date',
  // Single option => the "View by" toggle is hidden; donations are ranked by order date.
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: TOP_DONORS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingDonationsTopDonorsColumns_${teamId}`,
  firstColumnId: 'donor_name',
  defaultSortRules: [{ field: 'total_donated', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/top-donors/data.json`,
  detailResponseKey: 'donors',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/top-donors/export`,
  detailErrorMessage: 'Failed to load donor data.',
  detailTableTitle: 'Top Donors',
  emptyMessage: 'No donations in this period.',
  searchPlaceholder: 'Search donors...',
  paginationLabel: 'donors',
  itemLabel: (count) => `donor${count !== 1 ? 's' : ''}`,

  buildFilterGroups,
  buildDetailApiFilters,
  filterTitle: 'Filter Donors',
  filterSubtitle: 'Apply filters to narrow donor data.',

  buildKpiCards: (current, prior) => [
    { label: 'Total Raised', value: compactCurrency(current.total_raised || 0), current: current.total_raised || 0, prior: prior.total_raised || 0 },
    { label: 'Unique Donors', value: (current.donor_count || 0).toLocaleString(), current: current.donor_count || 0, prior: prior.donor_count || 0 },
    { label: 'Total Gifts', value: (current.gift_count || 0).toLocaleString(), current: current.gift_count || 0, prior: prior.gift_count || 0 },
    { label: 'Average Gift', value: currencyFormatter.format(current.avg_gift || 0), current: current.avg_gift || 0, prior: prior.avg_gift || 0 },
  ],
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
    switch (col.id) {
      case 'donor_name': {
        const name = row.donor_name || '(No name)';
        if (team?.package?.crm && row.id) {
          return (
            <a href={`/teams/${team.id}/crm/fans/${row.id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
              {name}
            </a>
          );
        }
        return name;
      }
      case 'email': return row.email || '—';
      case 'total_donated': return cf.format(row.total_donated || 0);
      case 'gift_count': return (row.gift_count || 0).toLocaleString();
      case 'avg_gift': return cf.format(row.avg_gift || 0);
      case 'last_gift_at': return <span className="text-nowrap">{row.last_gift_at ? formatEventDate(row.last_gift_at) : '—'}</span>;
      default: return '—';
    }
  },
};

function ReportingDonationsTopDonorsPage(props) {
  return <SalesDetailPage {...props} config={TOP_DONORS_CONFIG} />;
}

export default ReportingDonationsTopDonorsPage;
